import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  GitBranch, 
  AlertCircle, 
  CheckCircle2,
  Building2,
  Users,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const BUSINESS_TYPES = {
  restaurant: { label: 'Restaurant', icon: '🍽️', color: '#9A031E' },
  grocery: { label: 'Grocery Store', icon: '🛒', color: '#10B981' },
  service: { label: 'Service Provider', icon: '🔧', color: '#3B82F6' }
};

export default function AdminWorkflowManagement() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessType, setSelectedBusinessType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, [selectedBusinessType]);

  const fetchTemplates = async () => {
    try {
      const params = selectedBusinessType !== 'all' ? { business_type: selectedBusinessType } : {};
      
      const response = await api.get('/admin/workflow-templates', { params });
      
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load workflow templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this workflow template? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/workflow-templates/${templateId}`);
      
      toast.success('Workflow template deleted successfully');
      fetchTemplates();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete template';
      toast.error(message);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" data-testid="admin-workflow-management">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-4xl font-bold mb-2" 
              style={{ color: '#222146', fontFamily: 'DM Serif Display, serif' }}
              data-testid="page-title"
            >
              Workflow Management
            </h1>
            <p className="text-gray-600">
              Create and manage order workflow templates for different business types
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/workflows/create')}
            className="flex items-center gap-2"
            style={{ backgroundColor: '#9A031E' }}
            data-testid="create-workflow-btn"
          >
            <Plus size={20} />
            Create Workflow
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search workflows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Business Type Filter */}
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-500" />
              <select
                value={selectedBusinessType}
                onChange={(e) => setSelectedBusinessType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#9A031E] focus:border-transparent"
                data-testid="business-type-filter"
              >
                <option value="all">All Types</option>
                {Object.entries(BUSINESS_TYPES).map(([key, { label, icon }]) => (
                  <option key={key} value={key}>{icon} {label}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-50">
                <GitBranch size={24} style={{ color: '#3B82F6' }} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Templates</p>
                <p className="text-2xl font-bold" style={{ color: '#222146' }}>{templates.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-50">
                <Building2 size={24} style={{ color: '#9A031E' }} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Workflows</p>
                <p className="text-2xl font-bold" style={{ color: '#222146' }}>
                  {templates.reduce((acc, t) => acc + t.usage_count, 0)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-50">
                <CheckCircle2 size={24} style={{ color: '#10B981' }} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Default Templates</p>
                <p className="text-2xl font-bold" style={{ color: '#222146' }}>
                  {templates.filter(t => t.is_default).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-50">
                <Users size={24} style={{ color: '#8B5CF6' }} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Business Types</p>
                <p className="text-2xl font-bold" style={{ color: '#222146' }}>3</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Template List */}
        {filteredTemplates.length === 0 ? (
          <Card className="p-12 text-center">
            <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2" style={{ color: '#222146' }}>
              No workflows found
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery ? 'Try adjusting your search criteria' : 'Create your first workflow template to get started'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate('/admin/workflows/create')}
                style={{ backgroundColor: '#9A031E' }}
              >
                <Plus size={20} className="mr-2" />
                Create Workflow
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTemplates.map((template) => {
              const businessType = BUSINESS_TYPES[template.business_type] || {};
              
              return (
                <Card 
                  key={template.id} 
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  data-testid={`workflow-template-${template.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold" style={{ color: '#222146' }}>
                          {template.name}
                        </h3>
                        {template.is_default && (
                          <Badge 
                            className="text-xs"
                            style={{ backgroundColor: '#10B981', color: 'white' }}
                          >
                            Default
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{businessType.icon}</span>
                        <span className="text-sm font-medium" style={{ color: businessType.color }}>
                          {businessType.label}
                        </span>
                      </div>
                      {template.description && (
                        <p className="text-sm text-gray-600">{template.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 mb-4 py-3 border-y border-gray-100">
                    <div className="flex items-center gap-2">
                      <GitBranch size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {template.stages?.length || 0} stages
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {template.usage_count} vendor{template.usage_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => navigate(`/admin/workflows/${template.id}`)}
                      data-testid={`view-workflow-${template.id}`}
                    >
                      <Edit2 size={16} className="mr-2" />
                      View & Edit
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                      disabled={template.usage_count > 0}
                      data-testid={`delete-workflow-${template.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  
                  {template.usage_count > 0 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Cannot delete: in use by {template.usage_count} vendor(s)
                    </p>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
